import { inject as injectVercelAnalytics } from '@vercel/analytics'
import type { CaptureResult, PostHog, PostHogInterface } from 'posthog-js'
import { isCapturing, type Choices } from './consent'
import { scrubPath, scrubUrl } from './scrubUrl'

/**
 * Product analytics. Every event and property is listed in AnalyticsEvents
 * below and in docs/analytics.md; values are ids, counts and on/off flags
 * only. Never names, free text, emails, file names or URLs: those can
 * identify people or reveal their patterns.
 */
export type AnalyticsEvents = {
  // Marketing pages
  cta_clicked: { target: 'app' | 'builder'; placement: string }
  stitch_calculator_used: { mode: string; length_unit: string; spacing_unit: string }

  // Editor
  editor_opened: { start: 'cloud' | 'autosave' | 'builder' | 'blank' }
  tool_selected: { tool: string; via: 'toolbar' | 'shortcut' }
  shape_created: { kind: string }
  stitch_line_added: {
    path: 'free' | 'perimeter' | 'segments'
    distribution: string
    hole_shape: string
    spacing_mm?: number
    lines: number
  }
  chisel_preset_applied: { preset: string }
  hardware_placed: { hardware: string }
  template_placed: { shapes: number }
  template_saved: { cloud: boolean }
  template_published: Record<string, never>
  library_opened: { via: 'templates_pane' | 'menu' }
  mirror_created: { axis: 'x' | 'y' }
  layer_added: { layers: number }
  layer_renamed: Record<string, never>
  layer_toggled: { toggle: 'hidden' | 'locked'; on: boolean }
  layer_moved: Record<string, never>
  layer_deleted: { objects: number }
  layer_objects_moved: { count: number; across_layers: boolean }
  marking_set: { marking: 'fold' | 'cutout'; on: boolean }
  images_inserted: { count: number; source: 'upload' | 'gallery' }
  pdf_imported: { pages: number }
  image_scale_set: Record<string, never>
  variable_added: Record<string, never>
  help_opened: { topic: string }
  help_topic_viewed: { topic: string }
  grid_toggled: { on: boolean }
  grid_nudge: { outcome: 'shown' | 'grid-on' | 'dismissed' }
  units_changed: { unit: 'mm' | 'in' }

  // Projects and output
  project_created: Record<string, never>
  project_opened: { from: 'cloud' | 'file' }
  project_saved: { to: 'cloud' | 'file' }
  project_duplicated: Record<string, never>
  project_deleted: Record<string, never>
  export_opened: Record<string, never>
  pattern_exported: {
    format: 'pdf_tiled' | 'pdf_single' | 'lpat' | 'builder_pdf' | 'builder_print' | 'pattern_page_pdf'
    paper?: string
    orientation?: string
    watermark?: boolean
    dimensions?: boolean
    shapes?: number
    pages?: number
    wallet_type?: string
    from_builder?: boolean
  }

  // Account
  sign_in_prompted: { trigger: SignInTrigger }
  sign_in_started: { method: 'magic_link' | 'google' }
  signed_in: { new_account: boolean }
  signed_out: Record<string, never>
  data_exported: Record<string, never>
  account_deleted: Record<string, never>

  // Feedback
  feedback_sent: { has_email: boolean; has_attachment: boolean; rating?: number }
  checkin_dismissed: { final: boolean }

  // Wallet builder
  builder_opened: { view: 'list' | 'design' | 'shared' }
  wallet_started: { wallet_type: string }
  wallet_view_changed: { mode: string }
  /** The setting's name, such as `stitch.spacing`; never its value. */
  wallet_setting_changed: { setting: string; wallet_type: string }
  design_shared: { method: 'native' | 'copy' | 'link' }
  shared_design_opened: { ok: boolean }
  opened_in_editor: { wallet_type: string; review: boolean }

  // Privacy choices: how many people allow what, i.e. how much of the
  // picture the stored-ID data covers. Only sent while statistics are on.
  privacy_choice_made: { analytics: boolean; via: 'banner' | 'dialog' }

  // Something the person tried didn't work, and they were told so.
  action_failed: { action: FailedAction; reason: FailureReason }
  sync_conflict_resolved: { choice: 'copy' | 'mine' | 'cloud' }
}

/** What asked the person to sign in. */
export type SignInTrigger = 'sign_in_button' | 'images' | 'pdf_import' | 'builder_designs'

export type FailedAction =
  | 'pdf_export'
  | 'pdf_single_export'
  | 'lpat_export'
  | 'save_cloud'
  | 'save_file'
  | 'open_file'
  | 'open_cloud'
  | 'project_list'
  | 'project_delete'
  | 'project_duplicate'
  | 'sync_conflict'
  | 'sign_in_email'
  | 'sign_in_google'
  | 'image_upload'
  | 'image_gallery'
  | 'pdf_import'
  | 'template_save'
  | 'watermark_save'
  | 'data_export'
  | 'account_delete'
  | 'feedback_send'
  | 'builder_pdf'
  | 'builder_print'
  | 'builder_list'
  | 'builder_copy'
  | 'builder_delete'
  | 'builder_rename'
  | 'builder_share'
  | 'cutout_save'

/** A category, never the error message: messages can carry file names,
 *  addresses or other people's data. */
export type FailureReason =
  | 'error'
  | 'offline'
  | 'invalid_file'
  | 'too_large'
  | 'not_found'
  | 'page_too_small'
  | 'app_outdated'

export type AnalyticsEvent = keyof AnalyticsEvents
type Prop = string | number | boolean
export type Surface = 'site' | 'editor' | 'builder'

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
/** Same-origin path that vercel.json rewrites to PostHog's EU cloud, so the
 *  browser only ever talks to easypatt.com. */
const POSTHOG_API_HOST = '/ingest'
const POSTHOG_UI_HOST = 'https://eu.posthog.com'
const RELEASE = (import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA as string | undefined)?.slice(0, 7) ?? 'dev'

/** PostHog runs in production builds that have a project key. Vercel sets
 *  the key for Production only, so previews and local dev send nothing. */
export const productAnalyticsConfigured = import.meta.env.PROD && !!POSTHOG_KEY

const QUEUE_CAP = 50
/** Ad and social click ids PostHog lifts out of the address on its own
 *  (the list in @posthog/core). They identify one click, so they never go
 *  to analytics; utm_* campaign tags do. */
const CLICK_ID_PARAMS = new Set([
  'gad_source', 'mc_cid', 'gclid', 'gclsrc', 'dclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'twclid',
  'li_fat_id', 'igshid', 'ttclid', 'rdt_cid', 'epik', 'qclid', 'sccid', 'oppref', 'bfcid', 'irclid', '_kx',
])

let choices: Choices = { anonStats: false, analytics: false }
let started = false
let ph: PostHogInterface | null = null
let loadPromise: Promise<void> | null = null
let vercelInjected = false
let identifiedAs: string | null = null
let user: { id: string; createdAt: string } | null = null
const superProps: Record<string, Prop> = { release: RELEASE }
const queue: Array<[string, Record<string, Prop>]> = []
const readyWaiters: Array<() => void> = []

export function startAnalytics(surface: Surface, initial: Choices): void {
  if (started) return
  started = true
  superProps.surface = surface
  applyAnalyticsConsent(initial)
}

export function applyAnalyticsConsent(next: Choices): void {
  const prev = choices
  choices = { ...next }
  // Before PostHog has loaded, nothing can withdraw its stored id for us.
  if (!choices.analytics && !ph) clearStoredAnalyticsId()
  if (!isCapturing(choices)) {
    queue.length = 0
    // From here prepareEvent drops everything PostHog would still send.
    if (ph && prev.analytics) {
      ph.opt_out_capturing()
      identifiedAs = null
    }
    return
  }
  injectVercelOnce()
  if (!productAnalyticsConfigured) return
  if (!ph) {
    loadPostHogWhenIdle()
    return
  }
  if (choices.analytics && !prev.analytics) {
    ph.opt_in_capturing()
    syncIdentity()
  } else if (!choices.analytics && prev.analytics) {
    // Clears the stored ID; PostHog carries on cookie-free.
    ph.opt_out_capturing()
    identifiedAs = null
  }
}

export function track<E extends AnalyticsEvent>(event: E, props: AnalyticsEvents[E]): void {
  try {
    if (!started || !isCapturing(choices)) return
    const clean = cleanProps(props as Record<string, unknown>)
    if (ph) {
      ph.capture(event, clean)
    } else if (productAnalyticsConfigured && queue.length < QUEUE_CAP) {
      queue.push([event, clean])
    }
  } catch {
    // Analytics never breaks the app.
  }
}

/** For every error the person is shown. An unknown cause while the
 *  browser is offline is reported as `offline`. */
export function trackFailure(action: FailedAction, reason: FailureReason = 'error'): void {
  let offline = false
  try {
    offline = navigator.onLine === false
  } catch {
    // ignore
  }
  track('action_failed', { action, reason: reason === 'error' && offline ? 'offline' : reason })
}

/** Before leaving the page right after tracking something: loads PostHog now
 *  if it hasn't loaded yet, so the event gets out (PostHog sends what it
 *  holds when the page unloads). Never waits longer than `maxMs`. */
export function analyticsReady(maxMs: number): Promise<void> {
  if (ph || !started || !productAnalyticsConfigured || !isCapturing(choices)) return Promise.resolve()
  void loadPostHog()
  return new Promise((resolve) => {
    readyWaiters.push(resolve)
    window.setTimeout(resolve, maxMs)
  })
}

/** Signed-in state for every event; the account id is only attached when
 *  the visitor allowed product analytics. */
export function setAnalyticsUser(next: { id: string; createdAt: string } | null): void {
  user = next
  superProps.signed_in = !!next
  try {
    ph?.register({ signed_in: !!next })
    syncIdentity()
  } catch {
    // ignore
  }
}

function syncIdentity(): void {
  if (!ph) return
  if (user && choices.analytics) {
    if (identifiedAs === user.id) return
    // identify() from cookie-free mode would merge browsers into one person.
    if (ph.get_explicit_consent_status() !== 'granted') ph.opt_in_capturing()
    // The account's random id, never the email.
    ph.identify(user.id, undefined, { signed_up_on: user.createdAt.slice(0, 10) })
    identifiedAs = user.id
  } else if (!user && identifiedAs) {
    ph.reset()
    identifiedAs = null
    // reset() also forgets PostHog's record of the consent, which still holds.
    if (choices.analytics) ph.opt_in_capturing()
  }
}

function cleanProps(props: Record<string, unknown>): Record<string, Prop> {
  const out: Record<string, Prop> = {}
  for (const [k, v] of Object.entries(props)) {
    if (typeof v === 'string') out[k] = v.slice(0, 64)
    else if (typeof v === 'number' && Number.isFinite(v)) out[k] = Math.round(v * 100) / 100
    else if (typeof v === 'boolean') out[k] = v
  }
  return out
}

function clearStoredAnalyticsId(): void {
  if (!POSTHOG_KEY) return
  for (const store of [safeStorage('localStorage'), safeStorage('sessionStorage')]) {
    if (!store) continue
    try {
      const keys: string[] = []
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i)
        if (k && (k.startsWith(`ph_${POSTHOG_KEY}`) || k === `__ph_opt_in_out_${POSTHOG_KEY}`)) keys.push(k)
      }
      for (const k of keys) store.removeItem(k)
    } catch {
      // Storage blocked: nothing stored either.
    }
  }
}

function safeStorage(name: 'localStorage' | 'sessionStorage'): Storage | null {
  try {
    return window[name]
  } catch {
    return null
  }
}

function injectVercelOnce(): void {
  if (vercelInjected) return
  vercelInjected = true
  try {
    injectVercelAnalytics({
      beforeSend: (event) => (isCapturing(choices) ? { ...event, url: scrubUrl(event.url) } : null),
    })
  } catch {
    // ignore
  }
}

function loadPostHogWhenIdle(): void {
  if (loadPromise) return
  const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
    .requestIdleCallback
  const load = () => void loadPostHog()
  if (ric) ric(load, { timeout: 2500 })
  else window.setTimeout(load, 1200)
}

function loadPostHog(): Promise<void> {
  if (!loadPromise) {
    loadPromise = import('posthog-js')
      .then(({ default: posthog }) => initPostHog(posthog))
      .catch((e) => console.warn('Analytics failed to load:', e))
  }
  return loadPromise
}

function initPostHog(posthog: PostHog): void {
  if (!POSTHOG_KEY || ph) return
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_API_HOST,
    ui_host: POSTHOG_UI_HOST,
    defaults: '2026-08-30',
    // Until product analytics is allowed: nothing stored in the browser,
    // and PostHog counts visitors with a daily-salted hash it computes on
    // its servers (https://posthog.com/tutorials/cookieless-tracking).
    cookieless_mode: 'on_reject',
    opt_out_capturing_by_default: true,
    opt_out_persistence_by_default: true,
    opt_out_capturing_persistence_type: 'localStorage',
    persistence: 'localStorage',
    person_profiles: 'identified_only',
    // Explicit events only. Autocapture would also pick up on-screen text,
    // such as project names and the signed-in email.
    autocapture: false,
    capture_pageview: 'history_change',
    capture_pageleave: 'if_capture_pageview',
    capture_dead_clicks: false,
    capture_heatmaps: false,
    capture_exceptions: false,
    capture_performance: false,
    rageclick: false,
    // Recordings would show people's patterns, which we promise to keep private.
    disable_session_recording: true,
    disable_surveys: true,
    disable_external_dependency_loading: true,
    advanced_disable_flags: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    mask_personal_data_properties: true,
    before_send: prepareEvent,
    loaded: (instance) => {
      ph = instance
      instance.register(superProps)
      const granted = instance.get_explicit_consent_status() === 'granted'
      if (choices.analytics && !granted) instance.opt_in_capturing()
      else if (!choices.analytics && granted) instance.opt_out_capturing()
      syncIdentity()
      for (const [event, props] of queue.splice(0)) instance.capture(event, props)
      for (const resolve of readyWaiters.splice(0)) resolve()
    },
  })
}

const URL_KEY = /(url|referrer)$/i
const PATH_KEY = /pathname$/i

/** Last line of defence: drops events once statistics are switched off,
 *  strips URLs and click ids from whatever PostHog adds by itself, and puts
 *  back our own properties (surface, release, signed_in). PostHog's consent
 *  switches and reset() start a fresh identity without them, so events right
 *  after "Allow" or a sign-out would otherwise go out unlabelled. */
export function prepareEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event || !isCapturing(choices)) return null
  for (const [k, v] of Object.entries(superProps)) {
    if (event.properties[k] === undefined) event.properties[k] = v
  }
  scrubProps(event.properties)
  if (event.$set) scrubProps(event.$set)
  if (event.$set_once) scrubProps(event.$set_once)
  return event
}

function scrubProps(props: Record<string, unknown> | undefined): void {
  if (!props) return
  for (const [k, v] of Object.entries(props)) {
    const bare = k.startsWith('$initial_') ? k.slice('$initial_'.length) : k
    if (CLICK_ID_PARAMS.has(bare)) {
      delete props[k]
      continue
    }
    if (typeof v !== 'string' || v.startsWith('$')) continue
    if (URL_KEY.test(k)) props[k] = scrubUrl(v)
    else if (PATH_KEY.test(k)) props[k] = scrubPath(v)
  }
}

/** For tests. */
export function __setChoicesForTest(next: Choices): void {
  choices = next
}
